import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { campaignAPI } from '../services/api';

const brandColors = {
  gradient: {
    primary: 'linear-gradient(135deg, #7b2cbf 0%, #9d4edd 100%)',
    success: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
    warning: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
  },
};

const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openMediaViewerDialog, setOpenMediaViewerDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);

  useEffect(() => {
    fetchCampaignDetails();
  }, [id]);

  const fetchCampaignDetails = async () => {
    try {
      setLoading(true);
      const response = await campaignAPI.getCampaignById(id);
      console.log('Campaign response:', response.data);
      const campaignData = response.data?.data?.campaign || response.data?.data || response.data?.campaign || response.data;
      console.log('Campaign data:', campaignData);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      toast.error(error.response?.data?.message || 'Failed to load campaign details');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await campaignAPI.deleteCampaign(id);
      toast.success('Campaign deleted successfully');
      setOpenDeleteDialog(false);
      navigate('/campaigns');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to delete campaign');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      pending_approval: 'warning',
      approved: 'info',
      active: 'success',
      paused: 'warning',
      completed: 'default',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const formatDate = (date) => {
    if (!date || new Date(date).getTime() === 0) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return time;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box>
        <Alert severity="error">Campaign not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/campaigns')} sx={{ mt: 2 }}>
          Back to Campaigns
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/campaigns')} sx={{ background: '#f5f5f5' }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {campaign.campaignName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Campaign ID: {campaign.campaignId}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Chip
            label={getStatusLabel(campaign.status)}
            color={getStatusColor(campaign.status)}
            size="large"
            sx={{ fontWeight: 'bold', fontSize: '0.9rem', px: 2 }}
          />
          {campaign.status === 'draft' && (
            <>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/campaigns/edit/${campaign._id}`)}
              >
                Edit Campaign
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setOpenDeleteDialog(true)}
              >
                Delete Campaign
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Rejection Alert */}
      {campaign.status === 'rejected' && campaign.rejectionReason && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Rejection Reason:
          </Typography>
          <Typography variant="body2">{campaign.rejectionReason}</Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
              <PersonIcon color="primary" />
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Client Name
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {campaign.clientName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Content Type
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  {campaign.contentType === 'image' && <ImageIcon color="primary" />}
                  {campaign.contentType === 'video' && <VideoIcon color="primary" />}
                  <Typography variant="body1" fontWeight="500" textTransform="capitalize">
                    {campaign.contentType}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {campaign.duration} seconds
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Campaign Schedule */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
              <CalendarIcon color="primary" />
              Campaign Schedule
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {formatDate(campaign.startDate)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  End Date
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {formatDate(campaign.endDate)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Campaign Duration
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {Math.ceil(
                    (new Date(campaign.endDate) - new Date(campaign.startDate)) / (1000 * 60 * 60 * 24)
                  )}{' '}
                  days
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Media Files */}
        {campaign.mediaFiles && campaign.mediaFiles.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
                <PlayIcon color="primary" />
                Media Files ({campaign.mediaFiles.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {campaign.mediaFiles.map((media, index) => (
                  <Grid item xs={12} sm={6} md={4} key={media._id || index}>
                    <Card 
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        }
                      }}
                      onClick={() => {
                        setSelectedMediaFile(media);
                        setOpenMediaViewerDialog(true);
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          {media.contentType?.startsWith('image') ? (
                            <ImageIcon color="primary" />
                          ) : (
                            <VideoIcon color="primary" />
                          )}
                          <Typography variant="subtitle2" fontWeight="bold">
                            File {index + 1}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          {media.fileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Size: {(media.fileSize / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        {media.duration && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Duration: {media.duration}s
                          </Typography>
                        )}
                        {media.thumbnailUrl ? (
                          <Box
                            component="img"
                            src={`${process.env.REACT_APP_API_URL}${media.thumbnailUrl}`}
                            alt={media.fileName}
                            sx={{
                              width: '100%',
                              height: 120,
                              objectFit: 'cover',
                              borderRadius: 1,
                              mt: 1,
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `${process.env.REACT_APP_API_URL}${media.fileUrl}`;
                            }}
                          />
                        ) : media.contentType?.startsWith('image') ? (
                          <Box
                            component="img"
                            src={`${process.env.REACT_APP_API_URL}${media.fileUrl}`}
                            alt={media.fileName}
                            sx={{
                              width: '100%',
                              height: 120,
                              objectFit: 'cover',
                              borderRadius: 1,
                              mt: 1,
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: 120,
                              borderRadius: 1,
                              mt: 1,
                              backgroundColor: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#999',
                              fontSize: '14px',
                            }}
                          >
                            📹 Video - {media.fileName}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Legacy Single File */}
        {campaign.fileUrl && (!campaign.mediaFiles || campaign.mediaFiles.length === 0) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center" gap={1}>
                <PlayIcon color="primary" />
                Creative File
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" alignItems="center" gap={2}>
                {campaign.contentType === 'image' ? <ImageIcon color="primary" /> : <VideoIcon color="primary" />}
                <Box>
                  <Typography variant="body1" fontWeight="500">
                    {campaign.fileName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Size: {campaign.fileSize ? (campaign.fileSize / 1024 / 1024).toFixed(2) : 'N/A'} MB
                  </Typography>
                </Box>
              </Box>
              {campaign.thumbnailUrl ? (
                <Box
                  component="img"
                  src={`${process.env.REACT_APP_API_URL}${campaign.thumbnailUrl}`}
                  alt={campaign.fileName}
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 'auto',
                    borderRadius: 2,
                    mt: 2,
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `${process.env.REACT_APP_API_URL}${campaign.fileUrl}`;
                  }}
                />
              ) : campaign.contentType === 'image' ? (
                <Box
                  component="img"
                  src={`${process.env.REACT_APP_API_URL}${campaign.fileUrl}`}
                  alt={campaign.fileName}
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 'auto',
                    borderRadius: 2,
                    mt: 2,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 300,
                    borderRadius: 2,
                    mt: 2,
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '16px',
                  }}
                >
                  📹 Video Preview
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Timestamps */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body2">
                  {new Date(campaign.createdAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {new Date(campaign.updatedAt).toLocaleString()}
                </Typography>
              </Grid>
              {campaign.submittedAt && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted At
                  </Typography>
                  <Typography variant="body2">
                    {new Date(campaign.submittedAt).toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Media Viewer Dialog */}
        <Dialog
          open={openMediaViewerDialog}
          onClose={() => {
            setOpenMediaViewerDialog(false);
            setSelectedMediaFile(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedMediaFile?.fileName}
          </DialogTitle>
          <DialogContent>
            {selectedMediaFile?.contentType?.startsWith('image') ? (
              <Box
                component="img"
                src={`${process.env.REACT_APP_API_URL}${selectedMediaFile?.fileUrl}`}
                alt={selectedMediaFile?.fileName}
                sx={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
              />
            ) : (
              <video
                src={`${process.env.REACT_APP_API_URL}${selectedMediaFile?.fileUrl}`}
                controls
                autoPlay
                style={{ width: '100%', maxHeight: '600px' }}
              />
            )}
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                File Size: {selectedMediaFile ? (selectedMediaFile.fileSize / 1024 / 1024).toFixed(2) : 0} MB
              </Typography>
              {selectedMediaFile?.contentType?.startsWith('video') && (
                <Typography variant="body2" color="text.secondary">
                  Duration: {selectedMediaFile?.duration || 'N/A'} seconds
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenMediaViewerDialog(false);
              setSelectedMediaFile(null);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Campaign Dialog */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Delete Campaign</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete campaign "{campaign?.campaignName}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Box>
  );
};

export default CampaignDetails;